import { useState } from 'react'
import { Box, Drawer, IconButton, MenuItem, Typography, Divider } from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import CloseIcon from '@mui/icons-material/Close'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { PartnerPageData } from 'types/types'
import { FlowTypes } from '@/lib/constants'
import { getCharityPath, withoutCharitySlug } from '@/utils/charitySlug'
import { ContactUsDrawer } from './prize-draws/profile-drawer/ContactUsDrawer'

type LoggedOutHeaderMenuProps = {
  pageTheme: PartnerPageData
}

export default function LoggedOutHeaderMenu({ pageTheme }: LoggedOutHeaderMenuProps) {
  const [isOpen, setIsOpen] = useState(false)
  const router = useRouter()
  const toggleDrawer = (open: boolean) => () => {
    setIsOpen(open)
  }

  const handleLogin = () => {
    const slug = (router.query.slug as string) ?? pageTheme.company_domain
    const pathnameBase = `/${slug}/auth`
    const baseQuery = { ...withoutCharitySlug(router.query), flowType: FlowTypes.AUTH }

    if (pageTheme?.is_external_login) {
      router.push({
        pathname: `${pathnameBase}/external-login`,
        query: baseQuery,
      })
    } else {
      router.push({
        pathname: `${pathnameBase}/email-check`,
        query: baseQuery,
      })
    }
  }

  const handleNavigate = () => {
    setIsOpen(false)
  }

  const shouldShowPrizePage =
    Boolean(pageTheme?.feature_flags?.prize_advertisement) && Boolean(pageTheme?.prize_header)
  const shouldShowCharityPage = Boolean(pageTheme?.feature_flags?.sign_up_charity_page)

  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '65px' }}>
      <IconButton onClick={toggleDrawer(true)}>
        <MenuIcon sx={{ height: '24px', width: '24px', color: 'white' }} />
      </IconButton>
      <Drawer
        anchor="right"
        disableScrollLock
        open={isOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            width: '308px',
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: 'column',
            borderTopLeftRadius: '8px',
            borderBottomLeftRadius: '8px',
          },
        }}
        onClick={toggleDrawer(false)}
      >
        <Box sx={{ px: 3, py: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <IconButton onClick={toggleDrawer(false)} sx={{ height: '32px', width: '32px' }}>
              <CloseIcon sx={{ height: '32px', width: '32px', color: 'rgba(137, 137, 137, 1)' }} />
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Link
              href={`/${pageTheme?.company_domain}/gamerules${
                router.query.id ? `?gameId=${router.query.id}` : ''
              }`}
              onClick={handleNavigate}
            >
              <MenuItem sx={{ borderRadius: '8px' }}>
                <Typography
                  sx={{
                    fontFamily: pageTheme?.fonts?.secondary_font?.family,
                    fontWeight: 'bold',
                    fontStyle: 'italic',
                    color: pageTheme?.primary_button_color,
                  }}
                >
                  Game Rules
                </Typography>
              </MenuItem>
            </Link>
            {shouldShowPrizePage && (
              <Link
                href={`/${pageTheme?.company_domain}/prize${
                  router.query.id ? `?gameId=${router.query.id}` : ''
                }`}
                onClick={handleNavigate}
              >
                <MenuItem sx={{ borderRadius: '8px' }}>
                  <Typography
                    sx={{
                      fontFamily: pageTheme?.fonts?.secondary_font?.family,
                      fontWeight: 'bold',
                      fontStyle: 'italic',
                      color: pageTheme?.primary_button_color,
                    }}
                  >
                    Prize Page
                  </Typography>
                </MenuItem>
              </Link>
            )}
            {shouldShowCharityPage && (
              <Link href={getCharityPath(pageTheme)} onClick={handleNavigate}>
                <MenuItem sx={{ borderRadius: '8px' }}>
                  <Typography
                    sx={{
                      fontFamily: pageTheme?.fonts?.secondary_font?.family,
                      fontWeight: 'bold',
                      fontStyle: 'italic',
                      color: pageTheme?.primary_button_color,
                    }}
                  >
                    {pageTheme.charity_page_breadcrumb_name || 'Donate'}
                  </Typography>
                </MenuItem>
              </Link>
            )}
            <ContactUsDrawer
              partnerId={pageTheme.id}
              trigger={
                <MenuItem sx={{ borderRadius: '8px' }}>
                  <Typography
                    sx={{
                      fontFamily: pageTheme?.fonts?.secondary_font?.family,
                      fontWeight: 'bold',
                      fontStyle: 'italic',
                      color: pageTheme?.primary_button_color,
                    }}
                  >
                    Contact Us
                  </Typography>
                </MenuItem>
              }
            />
          </Box>
        </Box>
        <Divider sx={{ my: 0 }} />
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4, mt: 2 }}>
          <Typography
            onClick={handleLogin}
            sx={{
              cursor: 'pointer',
              color: 'rgba(137, 137, 137, 1)',
              textDecoration: 'underline',
              fontFamily: pageTheme?.fonts?.secondary_font?.family,
              fontWeight: 'bold',
              fontStyle: 'italic',
            }}
          >
            Log In
          </Typography>
        </Box>
      </Drawer>
    </Box>
  )
}
